import { useMutation, useQuery } from '@tanstack/react-query'
import moment from 'moment'
import { store } from '../..'
import { queryClient } from '../../..'
import { setAlert, setIsLoading } from '../../components/components-slice'
import { setInvoice } from './reducer'
import { invoices_received } from '../../../api/spend/invoice-received'
import { formatNumber } from '../../../utils/functions'

export function useGetInvoicePaySummary(limit, page, statusFilter, search) {
  return useQuery(
    ['vendor-invoice-pay-summary', { limit, page, statusFilter, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return invoices_received.getInvoicePaymentsSummary({
        limit,
        page,
        statusFilter,
        search,
      })
    },
    {
      select: (data) => {
        data?.data?.forEach((invoice_received) => {
          invoice_received.created = moment(invoice_received.created).format(
            'MMMM D, YYYY'
          )
          invoice_received.formatted_invoice_date = moment(
            invoice_received.invoice_date
          ).format('MMMM D, YYYY')
          invoice_received.formatted_invoice_due_date = moment(
            invoice_received.invoice_due_date
          ).format('MMMM D, YYYY')
          invoice_received.formatted_total_amount = formatNumber(
            invoice_received.total_amount,
            2
          )
          invoice_received.status = parseInt(invoice_received.status)
          switch (invoice_received.status) {
            case 0:
              invoice_received.computedActions = [0, 2]
              break
            case 1:
              // invoice_received.computedActions = [1]
              invoice_received.computedActions = [0]
              break
            case 2:
              invoice_received.computedActions = [3, 4]
              break
            case 3:
              invoice_received.computedActions = [0, 1]
              break
            default:
              break
          }
        })

        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useIdInvoicePayView(id, limit, page, statusFilter, search) {
  return useQuery(
    ['vendor-pay-by-id', { id, limit, page, statusFilter, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return invoices_received.fetchVendorInvoicePayById({
        id,
        limit,
        page,
        statusFilter,
        search,
      })
    },
    {
      select: (data) => {
        const transactionsData = data.transactions
        const mainData = data.mainData

        let total_net_pay = 0
        let total_trans_cost = 0

        transactionsData?.data?.forEach((item) => {
          item.status = mainData.status
          item.processed = mainData.processed

          if (item.processed === 1) {
            if (item.status === 1) {
              item.computedActions = [0]
            }
            // else if (item.status === 1) {
            //   item.computedActions = [2]
            // }
          }
          item.net_pay = parseFloat(item.net_pay)
          total_net_pay += parseFloat(item.net_pay)
          item.trans_cost
            ? (item.trans_cost = parseFloat(item.trans_cost))
            : (item.trans_cost = 100.00)
          total_trans_cost += parseFloat(item.trans_cost)
          item.total = formatNumber(
            item.trans_cost + parseFloat(item.net_pay),
            2
          )
          item.formatted_net_pay = formatNumber(item.net_pay, 2)
          if (!item.status) {
            item.status = 0
          }
          item.vendor = mainData.vendor
          item.vendor_email = mainData.vendor_email
          item.vendor_mobile = mainData.vendor_mobile
          item.invoice_number = mainData.invoice_number
          item.bank = mainData.bank
          item.account_number = mainData.account_number
        })

        if (transactionsData) {
          transactionsData.formatted_total_net_pay = formatNumber(
            total_net_pay,
            2
          )
          transactionsData.formatted_total_trans_cost = formatNumber(
            total_trans_cost,
            2
          )
          transactionsData.formatted_total_payable = formatNumber(
            total_net_pay + total_trans_cost,
            2
          )
          // Add mainData to the returned object
          transactionsData.mainData = mainData
        }

        return transactionsData
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useGetInvoicesReceived(limit, page, statusFilter, search) {
  return useQuery(
    ['invoices-received', { limit, page, statusFilter, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return invoices_received.getInvoicesReceived({
        limit,
        page,
        statusFilter,
        search,
      })
    },
    {
      select: (data) => {
        data?.data?.forEach((invoice_received) => {
          //compute action items
          invoice_received.created = moment(invoice_received.created).format(
            'MMMM D, YYYY'
          )
          invoice_received.formatted_invoice_date = moment(
            invoice_received.invoice_date
          ).format('MMMM D, YYYY')
          invoice_received.formatted_invoice_due_date = moment(
            invoice_received.invoice_due_date
          ).format('MMMM D, YYYY')
          invoice_received.formatted_total_amount = formatNumber(
            invoice_received.total_amount,
            2
          )
          if (invoice_received.status !== 1) {
            if (
              moment(invoice_received.invoice_due_date).diff(
                moment(invoice_received.invoice_date),
                'days'
              ) > 0
            ) {
              invoice_received.due_status = 0
            } else if (
              moment(invoice_received.invoice_due_date).diff(
                moment(invoice_received.invoice_date),
                'days'
              ) == 0
            ) {
              invoice_received.due_status = 2
            } else {
              invoice_received.due_status = 3
            }
          } else {
            invoice_received.due_status = '1'
          }

          switch (invoice_received.status) {
            case 0:
              invoice_received.computedActions = [0, 1, 2, 3, 4, 5]
              break
            case 1:
              invoice_received.computedActions = [2, 4]
              break
            case 2:
              invoice_received.computedActions = [0, 1, 2, 3, 4]
              break
            default:
              break
          }
        })

        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useGetInvoicesReceivedByVendor(
  id,
  limit,
  page,
  statusFilter,
  search
) {
  return useQuery(
    ['invoice-by-vendor', { id, limit, page, statusFilter, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return invoices_received.getInvoicesReceivedByVendor({
        id,
        limit,
        page,
        statusFilter,
        search,
      })
    },
    {
      select: (data) => {
        data?.data?.forEach((invoice_received) => {
          //compute action items
          invoice_received.created = moment(invoice_received.created).format(
            'MMMM D, YYYY'
          )
          invoice_received.invoice_date = moment(
            invoice_received.invoice_date
          ).format('MMMM D, YYYY')
          invoice_received.invoice_due_date = moment(
            invoice_received.invoice_due_date
          ).format('MMMM D, YYYY')
          invoice_received.formatted_total_amount = formatNumber(
            invoice_received.total_amount,
            2
          )
          if (invoice_received.status !== 1) {
            if (
              moment(invoice_received.invoice_due_date).diff(
                moment(invoice_received.invoice_date),
                'days'
              ) > 0
            ) {
              invoice_received.due_status = 0
            } else if (
              moment(invoice_received.invoice_due_date).diff(
                moment(invoice_received.invoice_date),
                'days'
              ) == 0
            ) {
              invoice_received.due_status = 2
            } else {
              invoice_received.due_status = 3
            }
          } else {
            invoice_received.due_status = '1'
          }
          switch (invoice_received.status) {
            case 0:
              invoice_received.computedActions = [0, 1, 2]
              break
            case 1:
              invoice_received.computedActions = [0, 1, 3]
              break
            default:
              break
          }
        })

        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useGetInvoiceReceivedPayments(
  id,
  limit,
  page,
  statusFilter,
  search
) {
  return useQuery(
    ['invoice-received-payments', { id, limit, page, statusFilter, search }],
    () => {
      store.dispatch(setIsLoading(true))
      return invoices_received.getInvoicesReceivedPayments({
        id,
        limit,
        page,
        statusFilter,
        search,
      })
    },
    {
      select: (data) => {
        data?.data?.forEach((invoice_received) => {
          //compute action items
          invoice_received.created = moment(invoice_received.created).format(
            'MMMM D, YYYY'
          )
          invoice_received.invoice_date = moment(
            invoice_received.invoice_date
          ).format('MMMM D, YYYY')
          invoice_received.invoice_due_date = moment(
            invoice_received.invoice_due_date
          ).format('MMMM D, YYYY')
          invoice_received.formatted_total_amount = formatNumber(
            invoice_received.total_amount,
            2
          )
          if (invoice_received.status !== 1) {
            if (
              moment(invoice_received.invoice_due_date).diff(
                moment(invoice_received.invoice_date),
                'days'
              ) > 0
            ) {
              invoice_received.due_status = 0
            } else if (
              moment(invoice_received.invoice_due_date).diff(
                moment(invoice_received.invoice_date),
                'days'
              ) == 0
            ) {
              invoice_received.due_status = 2
            } else {
              invoice_received.due_status = 3
            }
          } else {
            invoice_received.due_status = '1'
          }
          switch (invoice_received.status) {
            case 3:
              invoice_received.computedActions = [0, 1]
              break
            default:
              break
          }
        })

        return data
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
      // keepPreviousData: true
    }
  )
}

export function useGetInvoiceReceived(id) {
  return useQuery(
    ['invoice-received', { id }],
    () => {
      store.dispatch(setIsLoading(true))
      return invoices_received.getInvoiceReceived(id)
    },
    {
      enabled: id !== null,
      onSuccess: (response, variables, context) => {
        store.dispatch(setInvoice(response))
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useUpdateInvoiceReceived() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return invoices_received.updateInvoiceReceived(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('invoices-received')
        queryClient.invalidateQueries('invoice-received-by-purchase-order')
        queryClient.invalidateQueries('invoice-received')

        store.dispatch(
          setAlert(true, 'success', 'Invoice received updated successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useCreateSingleInvoiceReceived() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return invoices_received.createSingleInvoiceReceived(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        store.dispatch(setInvoice(response.data))
        queryClient.invalidateQueries('invoices-received')
        queryClient.invalidateQueries('invoice-received-by-purchase-order')
        queryClient.invalidateQueries('invoice-received')
        store.dispatch(
          setAlert(true, 'success', 'Invoice received added successfully')
        )
      },
      onSettled: (response, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useMarkInvoiceReceivedPaid() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return invoices_received.markInvoiceReceivedPaid(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('invoices-received')
        queryClient.invalidateQueries('invoice-received-by-purchase-order')
        queryClient.invalidateQueries('invoice-received')
        store.dispatch(
          setAlert(
            true,
            'success',
            'Invoice received status change successfully'
          )
        )
      },
      onSettled: (response, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useMarkInvoiceUnpaid() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return invoices_received.markInvoiceUnpaid(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('invoices-received')
        queryClient.invalidateQueries('invoice-received-by-purchase-order')
        queryClient.invalidateQueries('invoice-received')
        store.dispatch(
          setAlert(
            true,
            'success',
            'Invoice received status change successfully'
          )
        )
      },
      onSettled: (response, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useDeleteInvoiceReceived() {
  return useMutation(
    (payload) => {
      return invoices_received.deleteInvoiceReceived(payload)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('invoices-received')
        queryClient.invalidateQueries('invoice-received-by-purchase-order')
        queryClient.invalidateQueries('invoice-received')
        queryClient.invalidateQueries('invoice-received-payments')
        store.dispatch(
          setAlert(true, 'success', 'Invoice received deleted successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useInitiateInvoiceReceivedPayment() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return invoices_received.initiateInvoicePayment(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('invoices-received')
        queryClient.invalidateQueries('invoice-received-by-purchase-order')
        queryClient.invalidateQueries('invoice-received')
        queryClient.invalidateQueries('invoice-received-payments')
        store.dispatch(
          setAlert(true, 'success', 'Payment initiated successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useDisburseInvoiceReceivedPayment() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return invoices_received.disburseInvoicePayment(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('invoices-received')
        queryClient.invalidateQueries('invoice-received-by-purchase-order')
        queryClient.invalidateQueries('invoice-received')
        queryClient.invalidateQueries('invoice-received-payments')
        store.dispatch(
          setAlert(true, 'success', 'Payment disbursed successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}

export function useDeclineInvoiceReceivedPayment() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true))
      return invoices_received.declineInvoicePayment(payload)
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('invoices-received')
        queryClient.invalidateQueries('invoice-received-by-purchase-order')
        queryClient.invalidateQueries('invoice-received')
        queryClient.invalidateQueries('invoice-received-payments')
        store.dispatch(
          setAlert(true, 'success', 'Payment declined successfully')
        )
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false))
      },
    }
  )
}
